// 认证状态管理Hook - 纯Cookie认证版本
import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  ReactNode,
  useCallback,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserInfo,
  UserPermissions,
  logout as logoutApi,
  getCurrentUser,
  getUserPermissions,
} from '@/services/auth';
import { OFFER_CREATION_DRAFT_ID_STORAGE_KEY } from '@/features/offer/components/offer-creation-draft-utils';

// 认证状态枚举
enum AuthStatus {
  LOADING = 'loading',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated',
}

// 认证上下文类型
interface AuthContextType {
  user: UserInfo | null;
  permissions: UserPermissions | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  authStatus: AuthStatus;
  login: (user: UserInfo) => Promise<UserPermissions>;
  logout: () => Promise<void>;
  updateUser: (user: Partial<UserInfo>) => void;
  refreshAuth: () => Promise<void>;
}

// 创建认证上下文
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 认证提供者组件
interface AuthProviderProps {
  children: ReactNode;
}

const AUTH_REFRESH_COOLDOWN_MS = 5000;

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.LOADING);
  const navigate = useNavigate();
  const lastRefreshAtRef = useRef(0);

  const isLoading = authStatus === AuthStatus.LOADING;
  const isAuthenticated = authStatus === AuthStatus.AUTHENTICATED;

  const clearOfferCreationDraftSession = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(OFFER_CREATION_DRAFT_ID_STORAGE_KEY);
  }, []);

  const getFallbackPermissions = useCallback(
    (): UserPermissions => ({
      menu_permissions: [],
      data_scope: 'self',
      is_admin: false,
    }),
    []
  );

  // 刷新认证状态 - 纯Cookie认证版本
  const refreshAuth = useCallback(async () => {
    console.log('🔐 刷新认证状态...');
    lastRefreshAtRef.current = Date.now();

    try {
      const fetchedUser = await getCurrentUser();
      console.log('🔐 Cookie认证验证成功，获取用户信息:', fetchedUser);
      setUser(fetchedUser);

      // 获取用户权限
      try {
        const fetchedPermissions = await getUserPermissions();
        console.log('🔐 获取用户权限成功:', fetchedPermissions);
        setPermissions(fetchedPermissions);
      } catch (permError) {
        console.warn('🔐 获取用户权限失败，使用空权限兜底:', permError);
        setPermissions(getFallbackPermissions());
      }

      setAuthStatus(AuthStatus.AUTHENTICATED);
    } catch (error) {
      console.log('🔐 Cookie认证验证失败，设置为未认证状态:', error);
      setUser(null);
      setPermissions(null);
      setAuthStatus(AuthStatus.UNAUTHENTICATED);
    }
  }, [getFallbackPermissions]);

  // 初始化认证状态
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      console.log('🔐 开始初始化认证状态...');

      // 如果当前在OAuth回调页面，跳过初始化认证检查，等待OAuth流程完成
      if (window.location.pathname === '/oauth/callback') {
        console.log('🔐 当前在OAuth回调页面，跳过初始化认证检查');
        return;
      }

      await refreshAuth();

      if (isMounted) {
        console.log('🔐 认证状态初始化完成');
      }
    };

    void initAuth();

    return () => {
      isMounted = false;
    };
  }, [refreshAuth]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const refreshAuthWithCooldown = () => {
      if (window.location.pathname === '/oauth/callback') {
        return;
      }

      const now = Date.now();
      if (now - lastRefreshAtRef.current < AUTH_REFRESH_COOLDOWN_MS) {
        return;
      }

      void refreshAuth();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      refreshAuthWithCooldown();
    };

    const handleWindowFocus = () => {
      refreshAuthWithCooldown();
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshAuth]);

  // 登录
  const login = async (userInfo: UserInfo): Promise<UserPermissions> => {
    console.log('🔐 useAuth.login 被调用，用户信息:', userInfo);

    setUser(userInfo);
    setAuthStatus(AuthStatus.AUTHENTICATED);

    try {
      const fetchedPermissions = await getUserPermissions();
      console.log('🔐 登录后获取用户权限成功:', fetchedPermissions);
      setPermissions(fetchedPermissions);
      return fetchedPermissions;
    } catch (permError) {
      console.warn('🔐 登录后获取用户权限失败，使用空权限兜底:', permError);
      const fallbackPermissions = getFallbackPermissions();
      setPermissions(fallbackPermissions);
      return fallbackPermissions;
    }
  };

  // 退出登录
  const logout = useCallback(async () => {
    try {
      console.log('🔐 执行前台登出操作...');
      await logoutApi(); // 调用后端登出接口，清除服务器端 session/cookie
    } catch (error) {
      console.error('🔐 前台退出登录失败:', error);
    } finally {
      // 清除本地状态
      setUser(null);
      setPermissions(null);
      setAuthStatus(AuthStatus.UNAUTHENTICATED);
      clearOfferCreationDraftSession();

      // 前台退出后总是跳转到前台登录页
      console.log('🔐 前台登出，跳转到前台登录页');
      navigate('/login', { replace: true });
    }
  }, [clearOfferCreationDraftSession, navigate]);

  // 更新用户信息
  const updateUser = (updatedUser: Partial<UserInfo>) => {
    if (user) {
      const newUser = { ...user, ...updatedUser };
      setUser(newUser);
      console.log('🔐 用户信息已更新:', newUser);
    }
  };

  const value: AuthContextType = {
    user,
    permissions,
    isAuthenticated,
    isLoading,
    authStatus,
    login,
    logout,
    updateUser,
    refreshAuth,
  };

  return React.createElement(AuthContext.Provider, { value }, children);
}

// 使用认证Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// 前台路由守卫Hook
export function useAuthGuard() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // 前台未认证用户跳转到前台登录页
      console.log('🔐 前台用户未认证，跳转到前台登录页');
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  return { isAuthenticated, isLoading };
}
